import React, { useEffect, useRef, useState, useCallback } from "react";
import { useJsApiLoader } from '@react-google-maps/api';
import { googleMapsLoaderConfig } from '../config/googleMapsConfig';

// Use shared loader configuration to ensure consistency
const loaderConfig = {
  ...googleMapsLoaderConfig,
  preventGoogleFontsLoading: true,
};

const MapBackground = ({
  center = { lat: 23.0225, lng: 72.5714 },
  zoom = 12,
  pickupLocation = null,
  dropoffLocation = null,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(false);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  // Use the same loader as AmbulanceMap
  const { isLoaded, loadError } = useJsApiLoader(loaderConfig);

  // Clear existing markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      if (marker) marker.setMap(null);
    });
    markersRef.current = [];
  }, []);

  // Update markers without recreating map
  const updateMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !window.google) return;

    clearMarkers();

    // Add pickup marker
    if (pickupLocation) {
      const pickupMarker = new window.google.maps.Marker({
        position: {
          lat: pickupLocation.latitude,
          lng: pickupLocation.longitude,
        },
        map: mapInstanceRef.current,
        title: "Pickup Location",
        label: {
          text: "P",
          color: "white",
          fontWeight: "bold",
          fontSize: "14px",
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: "#10B981",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
          scale: 20,
        },
      });
      markersRef.current.push(pickupMarker);
    }

    // Add dropoff marker
    if (dropoffLocation) {
      const dropoffMarker = new window.google.maps.Marker({
        position: {
          lat: dropoffLocation.latitude,
          lng: dropoffLocation.longitude,
        },
        map: mapInstanceRef.current,
        title: "Dropoff Location",
        label: {
          text: "D",
          color: "white",
          fontWeight: "bold",
          fontSize: "14px",
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: "#EF4444",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
          scale: 20,
        },
      });
      markersRef.current.push(dropoffMarker);
    }

    // Fit bounds to show both locations
    if (pickupLocation && dropoffLocation) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(
        new window.google.maps.LatLng(
          pickupLocation.latitude,
          pickupLocation.longitude
        )
      );
      bounds.extend(
        new window.google.maps.LatLng(
          dropoffLocation.latitude,
          dropoffLocation.longitude
        )
      );
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [pickupLocation, dropoffLocation, clearMarkers]);

  // Initialize map only once when Google Maps is loaded
  useEffect(() => {
    if (isMapInitialized || !isLoaded) return;

    const initMap = () => {
      if (window.google && mapRef.current && !mapInstanceRef.current) {
        try {
          const map = new window.google.maps.Map(mapRef.current, {
            center: center,
            zoom: zoom,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: true,
            rotateControl: true,
            fullscreenControl: true,
          });

          mapInstanceRef.current = map;
          setIsMapInitialized(true);
          console.log("Map initialized successfully");
        } catch (error) {
          console.error("Error creating map:", error);
          if (error.message.includes("BillingNotEnabled")) {
            setMapError(true);
          }
        }
      }
    };

    // Initialize map when Google Maps is loaded
    setTimeout(initMap, 100);
  }, [center, zoom, isMapInitialized, isLoaded]);

  // Update markers when locations change
  useEffect(() => {
    if (isMapInitialized) {
      updateMarkers();
    }
  }, [pickupLocation, dropoffLocation, isMapInitialized, updateMarkers]);

  // Show loading state while Google Maps is loading
  if (!isLoaded) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{
          backgroundColor: "#e5e7eb",
          minHeight: "100vh",
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading map...</p>
        </div>
      </div>
    );
  }

  // Show error state if Google Maps failed to load
  if (loadError || mapError) {
    return (
      <div
        className="w-full h-full flex items-center justify-center"
        style={{
          backgroundColor: "#e5e7eb",
          minHeight: "100vh",
        }}
      >
        <div className="text-center p-8">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Map Background
          </h3>
          <p className="text-gray-600 mb-4">Ahmedabad, India</p>
          <div className="bg-white rounded-lg p-4 shadow-lg max-w-sm">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Note:</strong> Enable billing in Google Cloud Console to
              see the live map.
            </p>
            <p className="text-xs text-gray-500">
              Location: {center.lat.toFixed(4)}, {center.lng.toFixed(4)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full"
      style={{
        backgroundColor: "#e5e7eb",
        minHeight: "100vh",
      }}
    >
      {/* Google Maps container */}
      <div
        ref={mapRef}
        className="w-full h-full"
        style={{
          minHeight: "100vh",
          backgroundColor: "#f3f4f6",
        }}
      />
    </div>
  );
};

export default MapBackground;
